import {
    Box, Typography, Button, Modal, TextField, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, IconButton, Select, MenuItem,
    FormControl, InputLabel, Tooltip, Skeleton, ToggleButton, ToggleButtonGroup
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Toast } from 'primereact/toast';
import { Menu as PrimeMenu } from 'primereact/menu';
import AddIcon from '@mui/icons-material/Add';
import MoreHorizSharpIcon from '@mui/icons-material/MoreHorizSharp';
import CustomAxios from "../../utils/CustomAxios";
import { useSelector } from "react-redux";

// 0 = Sunday, matching CampaignDetails.repeat_days rather than inventing a
// second weekday convention for the same codebase.
const WEEKDAYS = [
    { value: 0, label: 'Sun' },
    { value: 1, label: 'Mon' },
    { value: 2, label: 'Tue' },
    { value: 3, label: 'Wed' },
    { value: 4, label: 'Thu' },
    { value: 5, label: 'Fri' },
    { value: 6, label: 'Sat' },
];

const EMPTY_SHIFT = {
    name: '',
    start_time: '09:00',
    end_time: '18:00',
    grace_minutes: 15,
    full_day_hours: '8.00',
    half_day_hours: '4.00',
    week_offs: [0],
    hub_id: '',
    is_active: true,
};

const EMPTY_DEPARTMENT = { name: '', hub_id: '', is_active: true };

const EMPTY_HOLIDAY = {
    name: '',
    date: '',
    hub_id: '',
    is_paid: true,
    is_active: true,
};

function ActiveChip({ active }) {
    return (
        <Box sx={{
            display: 'inline-flex', justifyContent: 'center',
            backgroundColor: active ? 'button.backgroundEdit' : '#FAFAFA',
            border: '1px solid', borderColor: active ? 'borders.green' : '#BFBFBF',
            borderRadius: '2px', padding: '1px 8px', minWidth: '72px'
        }}>
            <Typography sx={{
                fontSize: '11px', lineHeight: '20px',
                color: active ? 'borders.green' : 'text.secondary', textTransform: 'uppercase'
            }}>
                {active ? 'Active' : 'Inactive'}
            </Typography>
        </Box>
    );
}

export function StaffShiftSettings() {
    const selectedHub = useSelector(state => state.user.selected_hub);
    const inventory = useSelector(state => state.inventory.inventory);
    const toast = useRef(null);
    const shiftMenuRefs = useRef([]);
    const deptMenuRefs = useRef([]);

    const [shifts, setShifts] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [shiftModal, setShiftModal] = useState(false);
    const [shiftEditId, setShiftEditId] = useState(null);
    const [shiftForm, setShiftForm] = useState(EMPTY_SHIFT);

    const [deptModal, setDeptModal] = useState(false);
    const [deptEditId, setDeptEditId] = useState(null);
    const [deptForm, setDeptForm] = useState(EMPTY_DEPARTMENT);

    const [holidays, setHolidays] = useState([]);
    const [holidayYear, setHolidayYear] = useState(new Date().getFullYear());
    const [holidayModal, setHolidayModal] = useState(false);
    const [holidayEditId, setHolidayEditId] = useState(null);
    const [holidayForm, setHolidayForm] = useState(EMPTY_HOLIDAY);
    const holidayMenuRefs = useRef([]);

    const hubId = useMemo(() => {
        const normalize = (value) => {
            if (value === null || value === undefined) return null;
            if (Array.isArray(value)) return normalize(value[0]);
            const n = Number(value);
            return Number.isFinite(n) ? n : null;
        };
        const fromStore = normalize(selectedHub);
        if (fromStore) return fromStore;
        const stored = localStorage.getItem('selected_hub');
        if (!stored) return null;
        try {
            return normalize(JSON.parse(stored));
        } catch (e) {
            return normalize(stored);
        }
    }, [selectedHub]);

    const showToast = (severity, summary, detail) => {
        if (toast.current) toast.current.show({ severity, summary, detail, life: 3500 });
    };

    const errorMessage = (error, fallback) => {
        const detail = error?.response?.data?.error;
        if (!detail) return fallback;
        if (typeof detail === 'string') return detail;
        const firstKey = Object.keys(detail)[0];
        const firstValue = detail[firstKey];
        return Array.isArray(firstValue) ? firstValue[0] : String(firstValue);
    };

    useEffect(() => {
        if (!hubId) return;
        fetchAll();
    }, [hubId]);

    useEffect(() => {
        if (!hubId) return;
        fetchHolidays();
    }, [holidayYear]);

    const fetchAll = async () => {
        setLoading(true);
        await Promise.all([fetchShifts(), fetchDepartments(), fetchHolidays()]);
        setLoading(false);
    };

    const fetchHolidays = async () => {
        try {
            const res = await CustomAxios.get('staff/holidays/', {
                params: { hub_id: hubId, year: holidayYear },
            });
            setHolidays(res.data || []);
            holidayMenuRefs.current = (res.data || []).map((_, i) => holidayMenuRefs.current[i] || React.createRef());
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Failed to load holidays'));
        }
    };

    const openHolidayModal = (item = null) => {
        if (item) {
            setHolidayEditId(item.id);
            setHolidayForm({
                name: item.name || '', date: item.date || '',
                hub_id: item.hub || '', is_paid: item.is_paid, is_active: item.is_active,
            });
        } else {
            setHolidayEditId(null);
            setHolidayForm({ ...EMPTY_HOLIDAY, hub_id: '' });
        }
        setHolidayModal(true);
    };

    const saveHoliday = async () => {
        if (!holidayForm.name.trim() || !holidayForm.date) {
            showToast('error', 'Validation Error', 'Holiday name and date are both required');
            return;
        }
        const payload = { ...holidayForm, hub_id: holidayForm.hub_id || null };
        try {
            setSaving(true);
            if (holidayEditId) {
                await CustomAxios.put(`staff/holidays/${holidayEditId}/`, payload);
            } else {
                await CustomAxios.post('staff/holidays/', payload);
            }
            showToast('success', 'Success', holidayEditId ? 'Holiday updated' : 'Holiday added');
            setHolidayModal(false);
            fetchHolidays();
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Save failed'));
        } finally {
            setSaving(false);
        }
    };

    const deactivateHoliday = async (item) => {
        if (!window.confirm(`Remove "${item.name}"? Months already computed against it will change.`)) return;
        try {
            await CustomAxios.delete(`staff/holidays/${item.id}/`);
            showToast('success', 'Success', 'Holiday removed');
            fetchHolidays();
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Remove failed'));
        }
    };

    const fetchShifts = async () => {
        try {
            const res = await CustomAxios.get('staff/shifts/', { params: { hub_id: hubId } });
            setShifts(res.data || []);
            shiftMenuRefs.current = (res.data || []).map((_, i) => shiftMenuRefs.current[i] || React.createRef());
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Failed to load shifts'));
        }
    };

    const fetchDepartments = async () => {
        try {
            const res = await CustomAxios.get('staff/departments/', { params: { hub_id: hubId } });
            setDepartments(res.data || []);
            deptMenuRefs.current = (res.data || []).map((_, i) => deptMenuRefs.current[i] || React.createRef());
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Failed to load departments'));
        }
    };

    // -- shifts --------------------------------------------------------------

    const openShiftModal = (item = null) => {
        if (item) {
            setShiftEditId(item.id);
            setShiftForm({
                name: item.name || '',
                start_time: (item.start_time || '09:00').slice(0, 5),
                end_time: (item.end_time || '18:00').slice(0, 5),
                grace_minutes: item.grace_minutes ?? 15,
                full_day_hours: item.full_day_hours ?? '8.00',
                half_day_hours: item.half_day_hours ?? '4.00',
                week_offs: item.week_offs || [],
                hub_id: item.hub || '',
                is_active: item.is_active,
            });
        } else {
            setShiftEditId(null);
            setShiftForm({ ...EMPTY_SHIFT, hub_id: hubId || '' });
        }
        setShiftModal(true);
    };

    const saveShift = async () => {
        if (!shiftForm.name.trim()) {
            showToast('error', 'Validation Error', 'Shift name is required');
            return;
        }
        if (Number(shiftForm.half_day_hours) > Number(shiftForm.full_day_hours)) {
            showToast('error', 'Validation Error', 'Half-day hours cannot exceed full-day hours');
            return;
        }
        const payload = { ...shiftForm, hub_id: shiftForm.hub_id || null };
        try {
            setSaving(true);
            if (shiftEditId) {
                await CustomAxios.put(`staff/shifts/${shiftEditId}/`, payload);
            } else {
                await CustomAxios.post('staff/shifts/', payload);
            }
            showToast('success', 'Success', shiftEditId ? 'Shift updated' : 'Shift created');
            setShiftModal(false);
            fetchShifts();
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Save failed'));
        } finally {
            setSaving(false);
        }
    };

    const deactivateShift = async (item) => {
        if (!window.confirm(`Deactivate "${item.name}"? Past attendance judged against it is kept.`)) return;
        try {
            await CustomAxios.delete(`staff/shifts/${item.id}/`);
            showToast('success', 'Success', 'Shift deactivated');
            fetchShifts();
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Deactivate failed'));
        }
    };

    // -- departments ---------------------------------------------------------

    const openDeptModal = (item = null) => {
        if (item) {
            setDeptEditId(item.id);
            setDeptForm({ name: item.name || '', hub_id: item.hub || '', is_active: item.is_active });
        } else {
            setDeptEditId(null);
            setDeptForm({ ...EMPTY_DEPARTMENT, hub_id: hubId || '' });
        }
        setDeptModal(true);
    };

    const saveDepartment = async () => {
        if (!deptForm.name.trim()) {
            showToast('error', 'Validation Error', 'Department name is required');
            return;
        }
        const payload = { ...deptForm, hub_id: deptForm.hub_id || null };
        try {
            setSaving(true);
            if (deptEditId) {
                await CustomAxios.put(`staff/departments/${deptEditId}/`, payload);
            } else {
                await CustomAxios.post('staff/departments/', payload);
            }
            showToast('success', 'Success', deptEditId ? 'Department updated' : 'Department created');
            setDeptModal(false);
            fetchDepartments();
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Save failed'));
        } finally {
            setSaving(false);
        }
    };

    const deactivateDepartment = async (item) => {
        if (!window.confirm(`Deactivate "${item.name}"?`)) return;
        try {
            await CustomAxios.delete(`staff/departments/${item.id}/`);
            showToast('success', 'Success', 'Department deactivated');
            fetchDepartments();
        } catch (error) {
            showToast('error', 'Error', errorMessage(error, 'Deactivate failed'));
        }
    };

    const weekOffLabel = (days) => {
        if (!days || !days.length) return '—';
        return days.map(d => WEEKDAYS.find(w => w.value === d)?.label).filter(Boolean).join(', ');
    };

    const scopeLabel = (hub) => (hub ? (inventory?.find(i => i.value === hub)?.name || 'This hub') : 'Global');

    return (
        <Box sx={{ p: 3 }}>
            <Toast ref={toast} />

            <Box sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                gap: 2, flexWrap: 'wrap', mb: 3
            }}>
                <Typography variant="h5">Shifts, Departments &amp; Holidays</Typography>
                <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={() => openHolidayModal()}>
                        Add Holiday
                    </Button>
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={() => openDeptModal()}>
                        Add Department
                    </Button>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => openShiftModal()}>
                        Add Shift
                    </Button>
                </Box>
            </Box>

            <TableContainer component={Paper} sx={{ overflowX: 'auto', borderRadius: '12px', boxShadow: '0px 0px 25px 0px #00000012', mb: 3 }}>
                <Box sx={{ padding: '13px 18px', borderBottom: '1px solid #EDEDED', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>Shifts</Typography>
                    <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                        every threshold the attendance grid flags against lives here
                    </Typography>
                </Box>
                <Table>
                    <TableHead>
                        <TableRow>
                            {['Shift', 'Timing', 'Grace', 'Full day', 'Half day', 'Week-offs', 'Staff', 'Scope', 'Status', ''].map((h, i) => (
                                <TableCell key={i} sx={{ fontWeight: 600, backgroundColor: 'background.table', whiteSpace: 'nowrap' }}>{h}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={10} sx={{ backgroundColor: 'white', height: '140px' }}>
                                {[0, 1].map(i => <Skeleton key={i} animation="wave" sx={{ height: '46px', background: 'rgb(0 0 0 / 6%)' }} />)}
                            </TableCell></TableRow>
                        ) : shifts.length ? shifts.map((item, ind) => (
                            <TableRow key={item.id} sx={{ opacity: item.is_active ? 1 : 0.6 }}>
                                <TableCell sx={{ backgroundColor: 'white', fontWeight: 500 }}>
                                    {item.name}
                                    {item.crosses_midnight && (
                                        <Typography component="span" sx={{ ml: 1, fontSize: '10px', color: 'button.blue' }}>
                                            crosses midnight
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell sx={{ backgroundColor: 'white', whiteSpace: 'nowrap' }}>{item.timing_label}</TableCell>
                                <TableCell sx={{ backgroundColor: 'white' }}>{item.grace_minutes} min</TableCell>
                                <TableCell sx={{ backgroundColor: 'white' }}>{Number(item.full_day_hours).toFixed(1)} h</TableCell>
                                <TableCell sx={{ backgroundColor: 'white' }}>{Number(item.half_day_hours).toFixed(1)} h</TableCell>
                                <TableCell sx={{ backgroundColor: 'white' }}>{weekOffLabel(item.week_offs)}</TableCell>
                                <TableCell sx={{ backgroundColor: 'white' }}>{item.staff_count}</TableCell>
                                <TableCell sx={{ backgroundColor: 'white' }}>{scopeLabel(item.hub)}</TableCell>
                                <TableCell sx={{ backgroundColor: 'white' }}><ActiveChip active={item.is_active} /></TableCell>
                                <TableCell sx={{ backgroundColor: 'white' }}>
                                    <PrimeMenu
                                        model={[
                                            { label: 'Edit', command: () => openShiftModal(item) },
                                            { label: 'Deactivate', command: () => deactivateShift(item) },
                                        ]}
                                        popup ref={shiftMenuRefs.current[ind]} className="custom-menu"
                                        style={{ marginTop: '-10px' }}
                                    />
                                    <Tooltip title="Actions" arrow>
                                        <IconButton onClick={(e) => shiftMenuRefs.current[ind]?.current?.toggle(e)}>
                                            <MoreHorizSharpIcon />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        )) : (
                            <TableRow><TableCell colSpan={10} sx={{ textAlign: 'center', backgroundColor: 'white', height: '160px', fontWeight: 700 }}>
                                No shifts yet — add one before marking attendance
                            </TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TableContainer component={Paper} sx={{ overflowX: 'auto', borderRadius: '12px', boxShadow: '0px 0px 25px 0px #00000012', maxWidth: '760px' }}>
                <Box sx={{ padding: '13px 18px', borderBottom: '1px solid #EDEDED' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>Departments</Typography>
                </Box>
                <Table>
                    <TableHead>
                        <TableRow>
                            {['Department', 'Staff', 'Scope', 'Status', ''].map((h, i) => (
                                <TableCell key={i} sx={{ fontWeight: 600, backgroundColor: 'background.table' }}>{h}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {departments.length ? departments.map((item, ind) => (
                            <TableRow key={item.id} sx={{ opacity: item.is_active ? 1 : 0.6 }}>
                                <TableCell sx={{ backgroundColor: 'white', fontWeight: 500 }}>{item.name}</TableCell>
                                <TableCell sx={{ backgroundColor: 'white' }}>{item.staff_count}</TableCell>
                                <TableCell sx={{ backgroundColor: 'white' }}>{scopeLabel(item.hub)}</TableCell>
                                <TableCell sx={{ backgroundColor: 'white' }}><ActiveChip active={item.is_active} /></TableCell>
                                <TableCell sx={{ backgroundColor: 'white' }}>
                                    <PrimeMenu
                                        model={[
                                            { label: 'Edit', command: () => openDeptModal(item) },
                                            { label: 'Deactivate', command: () => deactivateDepartment(item) },
                                        ]}
                                        popup ref={deptMenuRefs.current[ind]} className="custom-menu"
                                        style={{ marginTop: '-10px' }}
                                    />
                                    <Tooltip title="Actions" arrow>
                                        <IconButton onClick={(e) => deptMenuRefs.current[ind]?.current?.toggle(e)}>
                                            <MoreHorizSharpIcon />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        )) : (
                            <TableRow><TableCell colSpan={5} sx={{ textAlign: 'center', backgroundColor: 'white', height: '140px', fontWeight: 700 }}>
                                No departments yet
                            </TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TableContainer component={Paper} sx={{ overflowX: 'auto', borderRadius: '12px', boxShadow: '0px 0px 25px 0px #00000012', mt: 3, maxWidth: '760px' }}>
                <Box sx={{ padding: '13px 18px', borderBottom: '1px solid #EDEDED', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>Holidays</Typography>
                        <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                            subtracted from working days; leave spanning one doesn't spend balance on it
                        </Typography>
                    </Box>
                    <FormControl size="small" sx={{ minWidth: '110px' }}>
                        <InputLabel>Year</InputLabel>
                        <Select value={holidayYear} label="Year" onChange={(e) => setHolidayYear(e.target.value)}>
                            {[0, 1, 2].map(offset => {
                                const year = new Date().getFullYear() - 1 + offset;
                                return <MenuItem key={year} value={year}>{year}</MenuItem>;
                            })}
                        </Select>
                    </FormControl>
                </Box>
                <Table>
                    <TableHead>
                        <TableRow>
                            {['Date', 'Holiday', 'Paid', 'Scope', 'Status', ''].map((h, i) => (
                                <TableCell key={i} sx={{ fontWeight: 600, backgroundColor: 'background.table' }}>{h}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {holidays.length ? holidays.map((item, ind) => (
                            <TableRow key={item.id} sx={{ opacity: item.is_active ? 1 : 0.6 }}>
                                <TableCell sx={{ backgroundColor: 'white', whiteSpace: 'nowrap' }}>
                                    {new Date(item.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                                </TableCell>
                                <TableCell sx={{ backgroundColor: 'white', fontWeight: 500 }}>{item.name}</TableCell>
                                <TableCell sx={{ backgroundColor: 'white' }}>
                                    <Typography sx={{ fontSize: '11px', color: item.is_paid ? 'borders.green' : 'text.secondary' }}>
                                        {item.is_paid ? 'Paid' : 'Unpaid'}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={{ backgroundColor: 'white' }}>{scopeLabel(item.hub)}</TableCell>
                                <TableCell sx={{ backgroundColor: 'white' }}><ActiveChip active={item.is_active} /></TableCell>
                                <TableCell sx={{ backgroundColor: 'white' }}>
                                    <PrimeMenu
                                        model={[
                                            { label: 'Edit', command: () => openHolidayModal(item) },
                                            { label: 'Remove', command: () => deactivateHoliday(item) },
                                        ]}
                                        popup ref={holidayMenuRefs.current[ind]} className="custom-menu"
                                        style={{ marginTop: '-10px' }}
                                    />
                                    <Tooltip title="Actions" arrow>
                                        <IconButton onClick={(e) => holidayMenuRefs.current[ind]?.current?.toggle(e)}>
                                            <MoreHorizSharpIcon />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        )) : (
                            <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', backgroundColor: 'white', height: '140px', fontWeight: 700 }}>
                                No holidays for {holidayYear}
                            </TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Holiday modal */}
            <Modal open={holidayModal} onClose={() => setHolidayModal(false)}>
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)', width: { xs: '92%', sm: 420 },
                    bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2
                }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>{holidayEditId ? 'Edit Holiday' : 'Add Holiday'}</Typography>
                    <TextField
                        fullWidth required label="Holiday name" placeholder="e.g. Independence Day"
                        value={holidayForm.name}
                        onChange={(e) => setHolidayForm({ ...holidayForm, name: e.target.value })}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        fullWidth required label="Date" type="date" value={holidayForm.date}
                        onChange={(e) => setHolidayForm({ ...holidayForm, date: e.target.value })}
                        InputLabelProps={{ shrink: true }} sx={{ mb: 2 }}
                    />
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Applies to</InputLabel>
                        <Select
                            value={holidayForm.hub_id} label="Applies to"
                            onChange={(e) => setHolidayForm({ ...holidayForm, hub_id: e.target.value })}
                        >
                            <MenuItem value="">Every hub (global)</MenuItem>
                            {(inventory || []).map(inv => (
                                <MenuItem key={inv.value} value={inv.value}>{inv.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Paid</InputLabel>
                        <Select
                            value={holidayForm.is_paid} label="Paid"
                            onChange={(e) => setHolidayForm({ ...holidayForm, is_paid: e.target.value })}
                        >
                            <MenuItem value={true}>Paid holiday</MenuItem>
                            <MenuItem value={false}>Unpaid holiday</MenuItem>
                        </Select>
                    </FormControl>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button onClick={() => setHolidayModal(false)}>Cancel</Button>
                        <Button variant="contained" onClick={saveHoliday} disabled={saving}>
                            {holidayEditId ? 'Update' : 'Create'}
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Shift modal */}
            <Modal open={shiftModal} onClose={() => setShiftModal(false)}>
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)', width: { xs: '92%', sm: 520 },
                    bgcolor: 'background.paper', boxShadow: 24, p: 4,
                    borderRadius: 2, maxHeight: '90vh', overflow: 'auto'
                }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>{shiftEditId ? 'Edit Shift' : 'Add Shift'}</Typography>

                    <TextField
                        fullWidth required label="Shift name" placeholder="e.g. Morning"
                        value={shiftForm.name}
                        onChange={(e) => setShiftForm({ ...shiftForm, name: e.target.value })}
                        sx={{ mb: 2 }}
                    />

                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField
                            fullWidth label="Start time" type="time" value={shiftForm.start_time}
                            onChange={(e) => setShiftForm({ ...shiftForm, start_time: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            fullWidth label="End time" type="time" value={shiftForm.end_time}
                            onChange={(e) => setShiftForm({ ...shiftForm, end_time: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            helperText={shiftForm.end_time <= shiftForm.start_time ? 'Crosses midnight' : ' '}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField
                            fullWidth label="Grace period (minutes)" type="number"
                            value={shiftForm.grace_minutes}
                            onChange={(e) => setShiftForm({ ...shiftForm, grace_minutes: e.target.value })}
                            helperText="Arrivals inside this are on time"
                        />
                        <FormControl fullWidth>
                            <InputLabel>Applies to</InputLabel>
                            <Select
                                value={shiftForm.hub_id} label="Applies to"
                                onChange={(e) => setShiftForm({ ...shiftForm, hub_id: e.target.value })}
                            >
                                <MenuItem value="">Every hub (global)</MenuItem>
                                {(inventory || []).map(inv => (
                                    <MenuItem key={inv.value} value={inv.value}>{inv.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField
                            fullWidth label="Full day hours" type="number" inputProps={{ step: '0.25' }}
                            value={shiftForm.full_day_hours}
                            onChange={(e) => setShiftForm({ ...shiftForm, full_day_hours: e.target.value })}
                        />
                        <TextField
                            fullWidth label="Half day hours" type="number" inputProps={{ step: '0.25' }}
                            value={shiftForm.half_day_hours}
                            onChange={(e) => setShiftForm({ ...shiftForm, half_day_hours: e.target.value })}
                            helperText="Below this, the day is absent"
                        />
                    </Box>

                    <Typography sx={{ fontSize: '11px', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.06em', mb: 1 }}>
                        Weekly offs
                    </Typography>
                    <ToggleButtonGroup
                        value={shiftForm.week_offs}
                        onChange={(e, value) => setShiftForm({ ...shiftForm, week_offs: value })}
                        size="small"
                        sx={{ mb: 1, flexWrap: 'wrap' }}
                    >
                        {WEEKDAYS.map(day => (
                            <ToggleButton key={day.value} value={day.value} sx={{ px: 1.5, fontSize: '11px' }}>
                                {day.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                    <Typography sx={{ fontSize: '10.5px', color: 'text.secondary', mb: 2 }}>
                        Working one of these earns a comp-off credit automatically.
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button onClick={() => setShiftModal(false)}>Cancel</Button>
                        <Button variant="contained" onClick={saveShift} disabled={saving}>
                            {shiftEditId ? 'Update' : 'Create'}
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Department modal */}
            <Modal open={deptModal} onClose={() => setDeptModal(false)}>
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)', width: { xs: '92%', sm: 420 },
                    bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2
                }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>{deptEditId ? 'Edit Department' : 'Add Department'}</Typography>
                    <TextField
                        fullWidth required label="Department name" placeholder="e.g. Store Ops"
                        value={deptForm.name}
                        onChange={(e) => setDeptForm({ ...deptForm, name: e.target.value })}
                        sx={{ mb: 2 }}
                    />
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Applies to</InputLabel>
                        <Select
                            value={deptForm.hub_id} label="Applies to"
                            onChange={(e) => setDeptForm({ ...deptForm, hub_id: e.target.value })}
                        >
                            <MenuItem value="">Every hub (global)</MenuItem>
                            {(inventory || []).map(inv => (
                                <MenuItem key={inv.value} value={inv.value}>{inv.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button onClick={() => setDeptModal(false)}>Cancel</Button>
                        <Button variant="contained" onClick={saveDepartment} disabled={saving}>
                            {deptEditId ? 'Update' : 'Create'}
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
}
